import React, { useState, useContext } from 'react';
import { modalContext } from '@/context/modal.context';
import { viewportContext } from '@/context/viewport.context';
import classes from './Documents.module.scss';
import moment from 'moment';
import Table from '@/components/Table/Table';
import t from '@/utils/Language/Documents-translations';
import ActionMenuDropdown from '@/components/ActionMenuDropdown/ActionMenuDropdown';
import 'react-loading-skeleton/dist/skeleton.css';
import mime from 'mime-types';
import uploadedDocuments from '@/utils/api/odas/uploaded_documents';
import reject from 'lodash/reject';
import truncate from 'lodash/truncate';
import useMixpanel from '@/hooks/useMixpanel';
import { toastContext } from '@/context/toast.context';
import TrueVaultAPI from '@/utils/api/truevault';
import { printDocumentFromFile } from '@/utils/utils';

const isUploaderOfAgencyClientDoc = (doc, agencyClientId) => {
  if (!agencyClientId) {
    return true;
  }
  return doc.is_uploader;
};

const menuList = (doc, unusedIsPhone, unusedIsXSPhone, agencyClientId, isPlusEnabled) => {
  let list = [
    { key: 'view', text: t('View'), icon: { font: 'eye', style: 'fas' } },
    { key: 'edit', text: t('Rename'), hidden: !isUploaderOfAgencyClientDoc(doc, agencyClientId), icon: { font: 'pen-square', style: 'fas' } },
    { key: 'manage', text: t('Share'), icon: { font: 'share-square', style: 'fas' }, hidden: agencyClientId ? true : false },
    {
      key: 'print',
      text: t('Print'),
      icon: { font: 'print', style: 'fas' },
      hidden: unusedIsXSPhone || unusedIsPhone || (agencyClientId && !isPlusEnabled) ? true : false
    },
    { key: 'delete', text: t('Delete'), icon: { font: 'trash-alt', style: 'fas' }, hidden: !isUploaderOfAgencyClientDoc(doc, agencyClientId) },
    { key: 'download', text: t('Download'), icon: { font: 'download', style: 'fas' }, hidden: agencyClientId && !isPlusEnabled ? true : false },
    { key: 'removeAccess', text: t('Remove my access'), icon: { style: 'fas', font: 'user-slash' }, hidden: !agencyClientId },
    { key: 'close', text: t('Close'), classes: classes.menuClose, icon: { style: 'fas', font: 'times' }, hidden: !unusedIsXSPhone && !unusedIsPhone }
  ];
  return list;
};

const getUploadedDocuments = async (enqueueModal, doc, agencyClientId) => {
  enqueueModal({
    name: 'LoadingModal',
    showCancel: false,
    title: t('Your document will begin downloading to your device shortly.'),
    preventClose: true
  });
  try {
    const response1 = await uploadedDocuments.getUploadedDocumentBlob(doc.id, agencyClientId);
    const response2 = await TrueVaultAPI.getBlob(response1.data.data.vault_id, response1.data.data.document_id, response1.data.data.access_token);
    return response2;
  } catch (err) {
    throw err;
  }
};

const onMenuSelectWithoutMethods = async (
  eventKey,
  doc,
  { documents, enqueueModal, addToast, closeModal, track, setSelectedDoc, agencyClientId, fetchDocuments, setDocuments }
) => {
  setSelectedDoc(doc);
  switch (eventKey) {
    case 'view':
      enqueueModal({
        name: 'ViewDocumentModal',
        document: doc,
        agencyClientId: agencyClientId,
        size: 'xl'
      });
      break;
    case 'delete':
      enqueueModal({
        name: 'ConfirmationModal',
        title: t('Are you sure you want to delete this document?'),
        text: t('This will delete it for you and all others who have access'),
        confirmButtonText: t('Yes, delete document'),
        declineButtonText: t("No, don't delete document"),
        onConfirm: () => {
          uploadedDocuments
            .deleteUploadedDocument(doc.id, agencyClientId)
            .then((resp) => {
              setDocuments(
                reject(documents, (deleted_doc) => {
                  return deleted_doc.id == doc.id;
                })
              );
              addToast({
                message: t('Document successfully deleted'),
                type: 'success'
              });
            })
            .catch((error) => {
              addToast({
                message: t('Document failed to be deleted. Please try again.'),
                type: 'error'
              });
            });
        },
        onError: (error) => {
          addToast({
            message: t('Document failed to be deleted. Please try again.'),
            type: 'error'
          });
          closeModal();
        }
      });
      break;
    case 'edit':
      enqueueModal({
        name: 'ViewDocumentModal',
        document: doc,
        agencyClientId: agencyClientId,
        size: 'xl',
        editMode: true,
        onUpdateSuccess: (resp) => {
          setDocuments((prevState) => {
            return [...prevState.map((oldDoc) => (oldDoc.id === resp?.data?.uploaded_document?.id ? resp.data.uploaded_document : oldDoc))];
          });
        }
      });
      break;
    case 'manage':
      enqueueModal({
        name: 'ShareDocumentModal',
        document: doc,
        track: track,
        agencyClientId: agencyClientId,
        fetchDocuments: fetchDocuments
      });
      break;
    case 'print':
      try {
        const response = await getUploadedDocuments(enqueueModal, doc, agencyClientId);
        const fileExtension = '.' + mime.extension(doc.mime_type);
        let base64Encoded = Buffer.from(response.data, 'binary').toString('base64');
        printDocumentFromFile({
          fileExtension,
          fileType: 'base64',
          file: base64Encoded
        });

        addToast({ message: t('Document successfully downloaded.'), type: 'success' });
        closeModal();
      } catch (error) {
        closeModal();
        addToast({ message: t('Sorry! The download was unsuccessful.'), type: 'error' });
      }
      break;

    // currently print and download do the same thing
    case 'download':
      try {
        const response = await getUploadedDocuments(enqueueModal, doc, agencyClientId);
        const fileExtension = '.' + mime.extension(doc.mime_type);
        let base64Encoded = Buffer.from(response.data, 'binary').toString('base64');
        const a = buildDownloadTag('data:' + response.headers['content-type'] + ';base64,' + base64Encoded, doc, fileExtension);
        a.click();
        closeModal();
        addToast({ message: t('Document successfully downloaded.'), type: 'success' });
      } catch (error) {
        closeModal();
        addToast({ message: t('Sorry! The download was unsuccessful.'), type: 'error' });
      }
      break;
    case 'removeAccess':
      enqueueModal({
        name: 'ConfirmationModal',
        title: t('Are you sure you want to remove your access to this document?'),
        text: t('You will no longer be able to view this document. This can be helpful if a document was shared with you by mistake.'),
        confirmButtonText: t('Yes, remove access'),
        declineButtonText: t("No, don't remove access"),
        onConfirm: () => {
          uploadedDocuments
            .removeAccessToDocument(doc.id, agencyClientId)
            .then((resp) => {
              setDocuments(
                reject(documents, (deleted_doc) => {
                  return deleted_doc.id == doc.id;
                })
              );
              addToast({
                message: t('Access successfully removed.'),
                type: 'success'
              });
            })
            .catch((error) => {
              addToast({ message: t('Failed to remove access. Please try again.'), type: 'error' });
              closeModal();
            });
        },
        onError: (error) => {
          addToast({
            message: t('Failed to remove access. Please try again.'),
            type: 'error'
          });
          closeModal();
        }
      });
      break;
    case 'close':
      //menu automatically closes onSelect, no action needed here
      break;
  }
};

const buildDownloadTag = (base64data, doc, fileExtension) => {
  let a = document.createElement('a');
  a.href = base64data; // Image as Base64 Goes here
  // add extension during download request if none exists
  a.download = doc.name.toLowerCase().indexOf(fileExtension) !== doc.name.length - fileExtension.length ? doc.name + fileExtension : doc.name; //File name Here
  return a;
};

const showCount = 5;
const dateFormat = 'MM/DD/YYYY h:mm A';

/**
 *  Because the layout of mobile vs desktop is quite different,
 *  provide different content and columns props to accomodate the change
 *
 * @returns {Component} Renders a list of documents, for each one of them
 */
const DocList = ({ agencyClientId, documents, setDocuments, fetchDocuments, isPlusEnabled }) => {
  const { enqueueModal, closeModal } = useContext(modalContext);
  const { unusedIsXSPhone, unusedIsPhone } = useContext(viewportContext);
  const [viewAll, setViewAll] = useState(false);
  const [, setSelectedDoc] = useState();
  const { track } = useMixpanel();
  const { addToast } = useContext(toastContext);

  const onMenuSelect = (eventKey, doc) => {
    onMenuSelectWithoutMethods(eventKey, doc, {
      documents,
      enqueueModal,
      addToast,
      closeModal,
      setSelectedDoc,
      agencyClientId,
      track,
      fetchDocuments,
      setDocuments
    });
  };

  const documentsForDisplay = () => {
    if (viewAll) {
      return documents;
    } else {
      return documents.slice(0, showCount);
    }
  };

  const onViewMore = () => {
    if (!viewAll) {
      setViewAll(true);
    }
  };

  return (
    <>
      {!unusedIsXSPhone && (
        <Table
          onViewMore={viewAll || documents.length < showCount + 1 ? false : onViewMore}
          content={documentsForDisplay().map((doc, index) => {
            doc.created_at = moment(doc.created_at).format(dateFormat);
            if (!agencyClientId) {
              doc.access = doc.shared_count > 0 ? t('Shared with count others', { count: doc.shared_count }) : t('Not shared');
            }
            doc.nameLink = (() => (
              <a
                className={classes.tableLink}
                href="#"
                onClick={(event) => {
                  event.preventDefault();
                  onMenuSelect('view', doc);
                }}
              >
                {truncate(doc.name, { length: 115, omission: '...' })}
              </a>
            ))();
            doc.uploaded_by_name = t(doc.uploaded_by_name);
            doc.menu = (() => (
              <ActionMenuDropdown
                menuList={menuList(doc, unusedIsPhone, unusedIsXSPhone, agencyClientId, isPlusEnabled)}
                onSelect={(eventKey) => {
                  onMenuSelect(eventKey, doc);
                }}
              />
            ))();
            return doc;
          })}
          columns={[
            { key: 'nameLink', title: t('Document Name') },
            { key: 'created_at', title: t('Uploaded On') },
            { key: 'uploaded_by_name', title: t('Uploaded By') },
            { key: 'access', title: t('Shared With'), hidden: agencyClientId ? true : false },
            { key: 'menu', title: t('Actions'), sortable: false }
          ]}
        />
      )}
      {unusedIsXSPhone && (
        <Table
          showHeader={false}
          onViewMore={viewAll || documents.length < showCount + 1 ? false : onViewMore}
          content={documentsForDisplay().map((doc, index) => {
            doc.content = (() => (
              <>
                {
                  (doc.nameLink = (() => (
                    <a
                      className={classes.tableLink}
                      href="#"
                      onClick={(event) => {
                        event.preventDefault();
                        onMenuSelect('view', doc);
                      }}
                    >
                      {truncate(doc.name, { length: 30, omission: '...' })}
                    </a>
                  ))())
                }
                {doc.created_at && <p className="my-0">{moment(doc.created_at).format(dateFormat)}</p>}
                {doc.uploaded_by_name && <p className="my-0">{t('Uploaded by') + ' ' + t(doc.uploaded_by_name)}</p>}
                {!agencyClientId && <p>{doc.shared_count > 0 ? t('Shared with count others', { count: doc.shared_count }) : t('Not shared')}</p>}
              </>
            ))();
            doc.menu = (() => (
              <ActionMenuDropdown
                menuList={menuList(doc, unusedIsPhone, unusedIsXSPhone, agencyClientId, isPlusEnabled)}
                onSelect={(eventKey) => {
                  onMenuSelect(eventKey, doc);
                }}
              />
            ))();
            return doc;
          })}
          columns={[
            { key: 'content', title: '', sortable: false },
            { key: 'menu', title: '', sortable: false, className: classes.verticalAlignMiddle }
          ]}
          responsive={false}
        />
      )}
    </>
  );
};

export default DocList;
